/**
 * Conductor v2 — the dual clock (R2 §1.2, amending D-001).
 *
 * Two clocks, one anchor:
 *  - realTime: monotone wall progress. SIM TICKS SCHEDULE AGAINST THIS
 *    (120 Hz in real seconds) — steering and gravity never dilate with
 *    throttle.
 *  - songTime = ∫ effRate · d(realTime): drives z, beat, beatPhase, and all
 *    beat-reactive visuals. The sim owns the exact per-tick integral; the
 *    conductor mirrors it via syncFromSim() and extrapolates continuously
 *    between ticks for rendering.
 *
 * The AudioConductor additionally schedules source.playbackRate along the
 * identical rate curve, so music position and songTime advance together by
 * construction — nothing to measure, nothing to resync.
 */
import type { Unsubscribe } from "../core/eventBus";
import { SIM_DT } from "../config/tuning";
import { BeatDispatcher } from "./beatDispatcher";

export interface SongMeta {
  id: string;
  bpm: number;
  /** Rows the ship advances per beat (typically 2 = one row per eighth note). */
  rowsPerBeat: number;
}

export interface Conductor {
  readonly bpm: number;
  /** Wall-clock progress in seconds (pause-aware). Sim ticks schedule on this. */
  readonly realTime: number;
  /** Musical time: ∫ effRate · d(realTime). */
  readonly songTime: number;
  /** songTime · bpm / 60 */
  readonly beat: number;
  readonly beatPhase: number;
  /** Advance (silent impl) / fire due beat callbacks. Call once per render frame. */
  tick(realDtSeconds: number): void;
  /** (Re)start both clocks at `fromSongTime` (rate 1). Negative = pre-roll. */
  restartAt(fromSongTime?: number): void;
  /** Anchor the song clock to the sim after stepping (tick, songTime, rate). */
  syncFromSim(tick: number, songTime: number, effRate: number): void;
  /** The sim's effRate changed at `tick` — audio impl schedules playbackRate. */
  rateChangedAt(tick: number, effRate: number): void;
  /** Continuous songTime at an arbitrary realTime (render interpolation). */
  songTimeAt(realT: number): number;
  onBeat(subdivision: number, cb: (beatIndex: number) => void): Unsubscribe;
  stop(): void;
}

/** Shared anchor math: songTime extrapolates linearly from the last sim sync. */
export class ClockAnchor {
  anchorRealT = 0;
  anchorSong = 0;
  rate = 1;

  reset(at: number): void {
    this.anchorRealT = at;
    this.anchorSong = at;
    this.rate = 1;
  }

  sync(tick: number, songTime: number, rate: number): void {
    this.anchorRealT = tick * SIM_DT;
    this.anchorSong = songTime;
    this.rate = rate;
  }

  songTimeAt(realT: number): number {
    return this.anchorSong + this.rate * (realT - this.anchorRealT);
  }
}

/** Fallback clock: pausing is simply not ticking, so time never jumps. */
export class SilentClock implements Conductor {
  readonly bpm: number;
  private rt = 0;
  private anchor = new ClockAnchor();
  private dispatcher = new BeatDispatcher();

  constructor(meta: SongMeta) {
    this.bpm = meta.bpm;
  }

  get realTime(): number {
    return this.rt;
  }

  get songTime(): number {
    return this.anchor.songTimeAt(this.rt);
  }

  get beat(): number {
    return (this.songTime * this.bpm) / 60;
  }

  get beatPhase(): number {
    const b = this.beat;
    return b - Math.floor(b);
  }

  tick(realDtSeconds: number): void {
    this.rt += realDtSeconds;
    if (this.beat >= 0) this.dispatcher.dispatch(this.beat);
  }

  restartAt(fromSongTime = 0): void {
    this.rt = fromSongTime;
    this.anchor.reset(fromSongTime);
    this.dispatcher.resetTo((fromSongTime * this.bpm) / 60);
  }

  syncFromSim(tick: number, songTime: number, effRate: number): void {
    this.anchor.sync(tick, songTime, effRate);
  }

  rateChangedAt(): void {
    // no audio to retune — anchors via syncFromSim are enough
  }

  songTimeAt(realT: number): number {
    return this.anchor.songTimeAt(realT);
  }

  onBeat(subdivision: number, cb: (beatIndex: number) => void): Unsubscribe {
    return this.dispatcher.on(subdivision, cb);
  }

  stop(): void {
    this.dispatcher.clear();
  }
}
